import React, { useState } from "react";
import { Row, Col } from "reactstrap";
import { Modal, ModalBody } from "reactstrap";
import { Spin } from "antd";
import { Eye, AlertCircle, FileText, X } from "lucide-react";
import {
  GridComponent,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Page,
  Filter,
  Toolbar,
  ExcelExport,
  Sort,
  Resize,
  Group,
  Freeze,
} from "@syncfusion/ej2-react-grids";
import { apiRequest } from "../../../utilities/apiMethod";

export default function PurchaseOrderGrid({ data = [] }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);

  let gridInstance;
  let modalGridInstance;

  const toolbarClick = (args) => {
    if (gridInstance && args.item.id === "PurchaseOrder_List_GridID_excelexport") {
      gridInstance.excelExport();
    }
  };

  const modalToolbarClick = (args) => {
    if (modalGridInstance && args.item.id === "PO_Detail_GridID_excelexport") {
      modalGridInstance.excelExport();
    }
  };

  const handleViewDetails = async (purchaseOrder) => {
    try {
      setLoading(true);
      setModalOpen(true);
      setSelectedPO(purchaseOrder);

      const response = await apiRequest(
        "GET",
        `SAPIntegration/getPurchaseOrderDetailByPONumber/${purchaseOrder}`
      );

      if (response.IsSuccess && response.Data) {
        setModalData(response.Data);
      } else {
        setModalData({ error: "No data found" });
      }
    } catch (error) {
      console.error("Error fetching purchase order details:", error);
      setModalData({ error: "Failed to fetch details" });
    } finally {
      setLoading(false);
    }
  };

  const serialNoTemplate = (props) => (
    <div style={{ textAlign: "center", fontWeight: 600, color: "#555" }}>
      {props.serialNo}
    </div>
  );

  const viewDetailsTemplate = (props) => (
    <div style={{ textAlign: "center" }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleViewDetails(props.PurchaseOrder);
        }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#1890ff",
          padding: "4px",
        }}
        type="button"
      >
        <Eye size={20} />
      </button>
    </div>
  );

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setModalData(null);
      setSelectedPO(null);
    }
  };

  const formatVal = (val) =>
    val !== null && val !== undefined && val !== "" ? String(val) : "—";

  const renderModalContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#8c9db5", fontSize: 14 }}>
            Fetching PO details…
          </p>
        </div>
      );
    }

    if (!modalData) return null;

    if (modalData.error) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "48px 24px",
            color: "#ff4d4f",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <AlertCircle size={40} color="#ff4d4f" />
          <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>{modalData.error}</p>
        </div>
      );
    }

    // Array response — Syncfusion grid with S No, pagination, search, Excel export
    if (Array.isArray(modalData) && modalData.length > 0) {
      const keys = Object.keys(modalData[0]);
      const enrichedModalData = modalData.map((row, idx) => ({ _sno: idx + 1, ...row }));

      return (
        <div>
          <p style={{ fontSize: 12, color: "#8c9db5", marginBottom: 12 }}>
            {modalData.length} line item{modalData.length !== 1 ? "s" : ""} found
          </p>
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              border: "1px solid #e8edf5",
              overflow: "hidden",
              boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
            }}
          >
            <GridComponent
              ref={(g) => (modalGridInstance = g)}
              id="PO_Detail_GridID"
              dataSource={enrichedModalData}
              allowPaging={true}
              allowSorting={true}
              allowFiltering={true}
              filterSettings={{ type: "Excel" }}
              toolbar={["ExcelExport", "Search"]}
              allowExcelExport={true}
              toolbarClick={modalToolbarClick}
              gridLines="Both"
              pageSettings={{ pageSizes: [10, 20, 50, "All"], pageCount: 5 }}
              rowHeight={32}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="_sno"
                  headerText="S No"
                  width="70"
                  textAlign="Center"
                  allowFiltering={false}
                  allowSorting={false}
                  allowExcelExport={false}
                />
                {keys.map((k) => (
                  <ColumnDirective
                    key={k}
                    field={k}
                    headerText={k}
                    width="160"
                    textAlign="Left"
                  />
                ))}
              </ColumnsDirective>
              <Inject services={[Page, Filter, Toolbar, ExcelExport, Sort, Resize]} />
            </GridComponent>
          </div>
        </div>
      );
    }

    // Object response — styled key-value table
    const d = Array.isArray(modalData) ? {} : modalData;
    return (
      <div
        style={{
          border: "1px solid #e8edf5",
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            {Object.entries(d).map(([key, value], i) => (
              <tr key={key} style={{ background: i % 2 === 0 ? "#fff" : "#f9fbff" }}>
                <td
                  style={{
                    padding: "9px 14px",
                    fontWeight: 600,
                    color: "#3d4f6e",
                    width: "38%",
                    borderBottom: "1px solid #edf1f9",
                    fontSize: 12,
                    verticalAlign: "top",
                  }}
                >
                  {key}
                </td>
                <td
                  style={{
                    padding: "9px 14px",
                    color: "#4a5568",
                    borderBottom: "1px solid #edf1f9",
                    wordBreak: "break-word",
                  }}
                >
                  {formatVal(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div
        className={`grid_compact ${isMobile ? "e-bigger" : ""}`}
        style={{
          position: "relative",
          marginTop: "10px",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          padding: "6px",
          marginBottom: "20px",
        }}
      >
        <Row>
          <Col>
            <GridComponent
              ref={(g) => (gridInstance = g)}
              id="PurchaseOrder_List_GridID"
              dataSource={data}
              allowPaging={true}
              allowSorting={true}
              allowFiltering={true}
              filterSettings={{ type: "Excel" }}
              toolbar={["ExcelExport", "Search"]}
              allowExcelExport={true}
              toolbarClick={toolbarClick}
              enableAdaptiveUI={isMobile}
              rowRenderingMode={isMobile ? "Vertical" : "Horizontal"}
              gridLines="Both"
              height={"500px"}
              pageSettings={{
                pageSizes: [10, 20, 30, 40, 50, "All"],
                pageCount: 10,
              }}
              rowHeight={isMobile ? "" : 32}
              frozenColumns={2}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="serialNo"
                  headerText="S No"
                  width="120"
                  textAlign="Center"
                  allowFiltering={false}
                  allowSorting={false}
                  template={serialNoTemplate}
                />
                <ColumnDirective
                  field="PurchaseOrder"
                  headerText="Purchase Order"
                  width="220"
                  textAlign="Left"
                  isPrimaryKey={true}
                />
                <ColumnDirective
                  field="StoreCode"
                  headerText="Store Code"
                  width="200"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="Supplier"
                  headerText="Supplier"
                  width="200"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="SupplierName"
                  headerText="Supplier Name"
                  width="250"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="PurchaseOrderDate"
                  headerText="PO Date"
                  width="220"
                  textAlign="Center"
                  type="date"
                  format="dd/MM/yyyy"
                />
                <ColumnDirective
                  field="PurchaseOrderTotalQuantity"
                  headerText="Total Quantity"
                  width="200"
                  textAlign="Right"
                />
                <ColumnDirective
                  field="PurchaseOrderTotalValue"
                  headerText="Total Value"
                  width="200"
                  textAlign="Right"
                  format="N2"
                />
                <ColumnDirective
                  field="PurchaseOrderLineItem"
                  headerText="Line Items"
                  width="180"
                  textAlign="Center"
                />
                <ColumnDirective
                  field="PoExpiryDate"
                  headerText="Expiry Date"
                  width="220"
                  textAlign="Center"
                  type="date"
                  format="dd/MM/yyyy"
                />
                <ColumnDirective
                  field="PoStatus"
                  headerText="Status"
                  width="180"
                  textAlign="Center"
                />
                <ColumnDirective
                  field="UploadedOn"
                  headerText="Uploaded On"
                  width="220"
                  textAlign="Center"
                  type="date"
                  format="dd/MM/yyyy"
                />
                <ColumnDirective
                  headerText="View Details"
                  width="150"
                  textAlign="Center"
                  template={viewDetailsTemplate}
                  allowFiltering={false}
                  allowSorting={false}
                  allowExcelExport={false}
                  freeze="Right"
                />
              </ColumnsDirective>
              <Inject
                services={[Page, Filter, Toolbar, ExcelExport, Sort, Group, Resize, Freeze]}
              />
            </GridComponent>
          </Col>
        </Row>
      </div>

      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        size="xl"
        centered
        scrollable
        style={{ maxWidth: "92vw", width: "92vw" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px",
            borderBottom: "2px solid #e8edf5",
            background: "#fff",
            borderRadius: "8px 8px 0 0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: "#eef3ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FileText size={18} color="#4361ee" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2740", lineHeight: 1.2 }}>
                Purchase Order Details
              </div>
              {selectedPO && (
                <div style={{ fontSize: 12, color: "#8c9db5", marginTop: 2 }}>
                  PO: {selectedPO}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={toggleModal}
            style={{
              background: "#f4f6fa",
              border: "1px solid #e0e6f0",
              borderRadius: 8,
              cursor: "pointer",
              padding: "6px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.15s",
            }}
            type="button"
          >
            <X size={16} color="#5a6a85" />
          </button>
        </div>
        <ModalBody
          style={{
            background: "#f8f9fb",
            padding: "24px",
            maxHeight: "75vh",
            overflowY: "auto",
          }}
        >
          {renderModalContent()}
        </ModalBody>
      </Modal>
    </>
  );
}
